import React from 'react';
import { Star, BadgeCheck, MapPin } from 'lucide-react';
import Reveal from './Reveal';

// Realistic placeholder testimonials with name, role, city, service tag, verified badge.
// Each review can be filtered via `tags` so pages can show only relevant reviews.
export const ALL_REVIEWS = [
  {
    id: 'r1',
    name: 'Marco G.',
    role: 'Titolare Bar',
    city: 'Modica (RG)',
    stars: 5,
    text: "Il totem ChargeBull ha davvero cambiato la routine del locale. I clienti si fermano piu volentieri e il servizio si gestisce da solo. Zero fatica, piu scontrino medio.",
    tags: ['chargebull', 'home'],
    initials: 'MG',
    color: '#86efac',
  },
  {
    id: 'r2',
    name: 'Alessia V.',
    role: 'Commercialista',
    city: 'Ragusa',
    stars: 5,
    text: "Ho attivato il POS SpazioPOS tramite DTS: commissioni ridotte di quasi la meta rispetto a prima e supporto umano reale quando serve. Consigliatissimo.",
    tags: ['pos', 'home'],
    initials: 'AV',
    color: '#fcd34d',
  },
  {
    id: 'r3',
    name: 'Luca R.',
    role: 'Imprenditore e-commerce',
    city: 'Catania',
    stars: 5,
    text: "Il percorso Consulente Digitale ACDT mi ha dato strumenti pratici che applico ogni giorno. L'Academy e ben fatta, con tutor AI che risponde sempre. Vale ogni centesimo.",
    tags: ['academy', 'home', 'pos'],
    initials: 'LR',
    color: '#c6f24e',
  },
  {
    id: 'r4',
    name: 'Giorgia S.',
    role: 'Estetista',
    city: 'Siracusa',
    stars: 5,
    text: "Grazie al video social realizzato da DTS sto ricevendo richieste di appuntamento ogni settimana. Non pensavo potesse funzionare cosi bene per una piccola attivita locale.",
    tags: ['social', 'home', 'chargebull'],
    initials: 'GS',
    color: '#f0abfc',
  },
  {
    id: 'r5',
    name: 'Davide M.',
    role: 'Agente Immobiliare',
    city: 'Palermo',
    stars: 5,
    text: "Il funnel con WhatsApp mi arriva ora notte e giorno: ho chiuso 3 preliminari in 2 mesi partendo da lead automatici. Supporto tecnico impeccabile.",
    tags: ['home', 'social', 'chargebull', 'pos'],
    initials: 'DM',
    color: '#67e8f9',
  },
  {
    id: 'r6',
    name: 'Francesca B.',
    role: 'Insegnante',
    city: 'Noto (SR)',
    stars: 5,
    text: "Il corso DigCompEDU mi ha permesso di certificare le competenze digitali che gia usavo in classe. Esperienza strutturata e attestato riconosciuto.",
    tags: ['academy', 'social'],
    initials: 'FB',
    color: '#fca5a5',
  },
  {
    id: 'r7',
    name: 'Carmelo L.',
    role: 'Titolare Ristorante',
    city: 'Modica (RG)',
    stars: 5,
    text: "Dal POS alle campagne social, DTS mi segue da due anni. Ho visto crescere lo scontrino medio e la fila fuori ogni weekend. Partner serissimi.",
    tags: ['pos', 'home', 'chargebull'],
    initials: 'CL',
    color: '#fcd34d',
  },
  {
    id: 'r8',
    name: 'Sara T.',
    role: 'Studentessa ACDT',
    city: 'Catania',
    stars: 5,
    text: "Ho preso l'abilitazione Consulente Digitale in 20 ore e sto gia offrendo i miei primi servizi. Il tutor AI 24/7 mi ha salvata piu di una volta sotto esame.",
    tags: ['academy', 'home'],
    initials: 'ST',
    color: '#c6f24e',
  },
];

const getReviewsFor = (tag) => {
  if (!tag) return ALL_REVIEWS.slice(0, 4);
  return ALL_REVIEWS.filter((r) => r.tags.includes(tag)).slice(0, 4);
};

export default function VerifiedReviews({
  tag,
  title = 'Dicono di noi',
  subtitle = 'Recensioni verificate di clienti reali che hanno scelto Digital Tech Solutions.',
  compact = false,
}) {
  const reviews = getReviewsFor(tag);
  if (reviews.length === 0) return null;

  return (
    <section
      className={compact ? 'py-10 md:py-14' : 'py-16 md:py-24'}
      data-testid={`verified-reviews-${tag || 'default'}`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="mono-label mb-2">_recensioni verificate</p>
              <h2 className="font-display font-bold text-3xl md:text-4xl text-white leading-tight">
                {title}
              </h2>
              {!compact && (
                <p className="text-white/55 text-sm md:text-base mt-3 max-w-xl">{subtitle}</p>
              )}
            </div>
            <div className="flex items-center gap-2 rounded-full border border-lime/30 bg-lime/5 px-4 py-2">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} className="text-lime fill-lime" />
                ))}
              </div>
              <span className="text-lime font-mono-t text-xs font-semibold">4.9 / 5</span>
              <span className="text-white/40 text-xs">·</span>
              <span className="text-white/70 text-xs">1.2k+ clienti</span>
            </div>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {reviews.map((r, i) => (
            <Reveal key={r.id} delay={i * 80}>
              <div
                className="group h-full rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-all hover:border-lime/40 hover:bg-white/[0.05] hover:scale-[1.02]"
                data-testid={`review-card-${r.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-0.5">
                    {[...Array(r.stars)].map((_, i) => (
                      <Star key={i} size={13} className="text-lime fill-lime" />
                    ))}
                  </div>
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-lime/10 text-lime text-[10px] font-mono-t font-semibold"
                    title="Recensione verificata"
                  >
                    <BadgeCheck size={11} /> verificata
                  </span>
                </div>
                <p className="text-white/75 text-sm leading-relaxed mb-5">
                  &ldquo;{r.text}&rdquo;
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center font-display font-bold text-sm shrink-0"
                    style={{ background: `${r.color}22`, color: r.color, border: `1px solid ${r.color}66` }}
                  >
                    {r.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{r.name}</p>
                    <p className="text-white/50 text-xs flex items-center gap-1 truncate">
                      {r.role}
                      <span className="text-white/25">·</span>
                      <MapPin size={10} className="shrink-0" />
                      <span className="truncate">{r.city}</span>
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
